export class MemoryChatRepository {
    chats = new Map();
    async listPeerIds() {
        return [...this.chats.keys()].sort();
    }
    async list(peerId, options = {}) {
        const limit = options.limit ?? 200;
        const list = [...(this.chats.get(peerId) || [])].sort(compareMessages);
        const filtered = filterBeforeCursor(list, options.beforeTs, options.beforeSeq);
        return filtered.slice(Math.max(0, filtered.length - limit));
    }
    async listFiles(peerId, options = {}) {
        const limit = options.limit ?? Number.MAX_SAFE_INTEGER;
        const list = [...(this.chats.get(peerId) || [])]
            .filter((message) => message.type !== 'deleted' && !!message.file)
            .sort(compareMessages);
        return list.slice(Math.max(0, list.length - limit));
    }
    async windowAround(peerId, messageId, options = {}) {
        const before = Math.max(0, Math.floor(Number(options.before ?? 30) || 0));
        const after = Math.max(0, Math.floor(Number(options.after ?? 30) || 0));
        const list = [...(this.chats.get(peerId) || [])].sort(compareMessages);
        const targetIndex = list.findIndex((message) => message.id === messageId);
        if (targetIndex < 0)
            return [];
        return list.slice(Math.max(0, targetIndex - before), Math.min(list.length, targetIndex + after + 1));
    }
    async put(peerId, message) {
        const list = this.chats.get(peerId) || [];
        const idx = list.findIndex((item) => item.id === message.id);
        if (idx >= 0)
            list[idx] = { ...list[idx], ...message };
        else
            list.push(message);
        this.chats.set(peerId, list.sort(compareMessages));
    }
    async putMany(peerId, messages) {
        for (const message of messages)
            await this.put(peerId, message);
    }
    async remove(peerId, messageId) {
        this.chats.set(peerId, (this.chats.get(peerId) || []).filter((item) => item.id !== messageId));
    }
    async clear(peerId) {
        this.chats.delete(peerId);
    }
}
export class IndexedDbChatRepository {
    namespace;
    dbName;
    storeName;
    version;
    dbPromise = null;
    constructor(namespace = 'default', dbName = 'SkyChatMobileChats', storeName = 'messages', version = 2) {
        this.namespace = namespace;
        this.dbName = dbName;
        this.storeName = storeName;
        this.version = version;
    }
    peerKey(peerId) {
        return `${this.namespace}\u001f${peerId}`;
    }
    messageKey(peerId, messageId) {
        return `${this.peerKey(peerId)}\u001f${messageId}`;
    }
    open() {
        if (this.dbPromise)
            return this.dbPromise;
        this.dbPromise = new Promise((resolve, reject) => {
            const req = indexedDB.open(this.dbName, this.version);
            req.onerror = () => reject(req.error);
            req.onsuccess = () => resolve(req.result);
            req.onupgradeneeded = () => {
                const db = req.result;
                const store = db.objectStoreNames.contains(this.storeName)
                    ? req.transaction?.objectStore(this.storeName)
                    : db.createObjectStore(this.storeName, { keyPath: 'key' });
                if (store && !store.indexNames.contains('byPeer')) {
                    store.createIndex('byPeer', 'peerKey', { unique: false });
                }
                if (store && !store.indexNames.contains('byPeerTs')) {
                    store.createIndex('byPeerTs', ['peerKey', 'ts', 'messageId'], { unique: false });
                }
                if (store && !store.indexNames.contains('byPeerFile')) {
                    store.createIndex('byPeerFile', ['peerKey', 'hasFile', 'ts', 'messageId'], { unique: false });
                }
                if (store) {
                    const cursorReq = store.openCursor();
                    cursorReq.onsuccess = () => {
                        const cursor = cursorReq.result;
                        if (!cursor)
                            return;
                        const normalized = normalizeStoredRecord(cursor.value);
                        cursor.update(normalized);
                        cursor.continue();
                    };
                }
            };
        });
        return this.dbPromise;
    }
    async listPeerIds() {
        const db = await this.open();
        const rows = await new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const req = tx.objectStore(this.storeName).getAll();
            req.onsuccess = () => resolve((req.result || []));
            req.onerror = () => reject(req.error);
        });
        return [...new Set(rows
                .filter((row) => row.namespace === this.namespace && row.peerId)
                .map((row) => row.peerId))].sort();
    }
    async list(peerId, options = {}) {
        const limit = normalizeLimit(options.limit, 200);
        const db = await this.open();
        const messages = await new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const store = tx.objectStore(this.storeName);
            const index = store.index('byPeerTs');
            const keyRange = IDBKeyRange.bound([this.peerKey(peerId), 0, ''], [this.peerKey(peerId), Number.MAX_SAFE_INTEGER, '\uffff']);
            const req = index.openCursor(keyRange, 'prev');
            const results = [];
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) {
                    resolve(sortChatMessages(results));
                    return;
                }
                const row = normalizeStoredRecord(cursor.value);
                if (isBeforeCursor(row.message, options.beforeTs, options.beforeSeq)) {
                    results.push(row.message);
                    if (results.length >= limit) {
                        resolve(sortChatMessages(results));
                        return;
                    }
                }
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
        });
        return messages;
    }
    async listFiles(peerId, options = {}) {
        const limit = normalizeLimit(options.limit, Number.MAX_SAFE_INTEGER);
        const db = await this.open();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const index = tx.objectStore(this.storeName).index('byPeerFile');
            const keyRange = IDBKeyRange.bound([this.peerKey(peerId), 1, 0, ''], [this.peerKey(peerId), 1, Number.MAX_SAFE_INTEGER, '\uffff']);
            const req = index.openCursor(keyRange, 'prev');
            const results = [];
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) {
                    resolve(sortChatMessages(results));
                    return;
                }
                const row = normalizeStoredRecord(cursor.value);
                if (row.message.type !== 'deleted' && row.message.file) {
                    results.push(row.message);
                    if (results.length >= limit) {
                        resolve(sortChatMessages(results));
                        return;
                    }
                }
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
        });
    }
    async windowAround(peerId, messageId, options = {}) {
        const before = Math.max(0, Math.floor(Number(options.before ?? 30) || 0));
        const after = Math.max(0, Math.floor(Number(options.after ?? 30) || 0));
        const db = await this.open();
        const target = await new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readonly');
            const req = tx.objectStore(this.storeName).get(this.messageKey(peerId, messageId));
            req.onsuccess = () => resolve(req.result ? normalizeStoredRecord(req.result) : null);
            req.onerror = () => reject(req.error);
        });
        if (!target)
            return [];
        const collect = (direction, range, count) => new Promise((resolve, reject) => {
            if (count <= 0) {
                resolve([]);
                return;
            }
            const tx = db.transaction(this.storeName, 'readonly');
            const req = tx.objectStore(this.storeName).index('byPeerTs').openCursor(range, direction);
            const results = [];
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor) {
                    resolve(results);
                    return;
                }
                const row = normalizeStoredRecord(cursor.value);
                if (row.message.id !== messageId) {
                    results.push(row.message);
                    if (results.length >= count) {
                        resolve(results);
                        return;
                    }
                }
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
        });
        const peerKey = this.peerKey(peerId);
        const olderRange = IDBKeyRange.bound([peerKey, 0, ''], [peerKey, target.ts, target.messageId], false, true);
        const newerRange = IDBKeyRange.bound([peerKey, target.ts, target.messageId], [peerKey, Number.MAX_SAFE_INTEGER, '\uffff'], true, false);
        const [older, newer] = await Promise.all([
            collect('prev', olderRange, before),
            collect('next', newerRange, after)
        ]);
        return sortChatMessages([...older, target.message, ...newer]);
    }
    async put(peerId, message) {
        const db = await this.open();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            tx.objectStore(this.storeName).put(recordFromMessage(this.namespace, peerId, message));
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    async putMany(peerId, messages) {
        if (!messages.length)
            return;
        const db = await this.open();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            const store = tx.objectStore(this.storeName);
            for (const message of messages) {
                store.put(recordFromMessage(this.namespace, peerId, message));
            }
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    async remove(peerId, messageId) {
        const db = await this.open();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            tx.objectStore(this.storeName).delete(this.messageKey(peerId, messageId));
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    async clear(peerId) {
        const db = await this.open();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(this.storeName, 'readwrite');
            const index = tx.objectStore(this.storeName).index('byPeer');
            const req = index.openCursor(IDBKeyRange.only(this.peerKey(peerId)));
            req.onsuccess = () => {
                const cursor = req.result;
                if (!cursor)
                    return;
                cursor.delete();
                cursor.continue();
            };
            req.onerror = () => reject(req.error);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
}
export function createChatRepository(namespace = 'default') {
    if (typeof indexedDB === 'undefined' || typeof IDBKeyRange === 'undefined') {
        return new MemoryChatRepository();
    }
    return new IndexedDbChatRepository(namespace);
}
function compareMessages(a, b) {
    const aSeq = Number.isFinite(Number(a.seq)) ? Number(a.seq) : null;
    const bSeq = Number.isFinite(Number(b.seq)) ? Number(b.seq) : null;
    if (aSeq !== null && bSeq !== null && aSeq !== bSeq)
        return aSeq - bSeq;
    if (a.ts !== b.ts)
        return a.ts - b.ts;
    return String(a.id).localeCompare(String(b.id));
}
function sortChatMessages(messages) {
    return [...messages].sort(compareMessages);
}
function peerKeyFor(namespace, peerId) {
    return `${namespace}\u001f${peerId}`;
}
function messageKeyFor(namespace, peerId, messageId) {
    return `${peerKeyFor(namespace, peerId)}\u001f${messageId}`;
}
function normalizeLimit(limit, fallback) {
    const value = Number(limit ?? fallback);
    if (!Number.isFinite(value) || value <= 0)
        return fallback;
    return Math.min(Math.floor(value), Number.MAX_SAFE_INTEGER);
}
function messageTs(message) {
    const value = Number(message.ts || 0);
    return Number.isFinite(value) && value > 0 ? value : 0;
}
function messageSearchText(message) {
    return `${message.plain || ''} ${message.file?.name || ''}`.trim().toLowerCase();
}
function recordFromMessage(namespace, peerId, message) {
    return {
        key: messageKeyFor(namespace, peerId, message.id),
        namespace,
        peerId,
        peerKey: peerKeyFor(namespace, peerId),
        messageId: message.id,
        ts: messageTs(message),
        hasFile: message.type !== 'deleted' && message.file ? 1 : 0,
        searchText: messageSearchText(message),
        message
    };
}
function normalizeStoredRecord(record) {
    const message = record.message;
    const namespace = record.namespace || 'default';
    const peerId = record.peerId || '';
    const messageId = record.messageId || message?.id || '';
    return {
        ...record,
        key: record.key || messageKeyFor(namespace, peerId, messageId),
        namespace,
        peerId,
        peerKey: record.peerKey || peerKeyFor(namespace, peerId),
        messageId,
        ts: message ? messageTs(message) : 0,
        hasFile: message && message.type !== 'deleted' && message.file ? 1 : 0,
        searchText: message ? messageSearchText(message) : ''
    };
}
function isBeforeCursor(message, beforeTs, beforeSeq) {
    const hasBeforeTs = Number.isFinite(Number(beforeTs)) && Number(beforeTs) > 0;
    const hasBeforeSeq = Number.isFinite(Number(beforeSeq));
    if (!hasBeforeTs && !hasBeforeSeq)
        return true;
    const tsCutoff = Number(beforeTs || 0);
    const seqCutoff = hasBeforeSeq ? Number(beforeSeq) : null;
    const messageSeq = Number.isFinite(Number(message.seq)) ? Number(message.seq) : null;
    if (seqCutoff !== null && messageSeq !== null)
        return messageSeq < seqCutoff;
    return Number(message.ts || 0) < tsCutoff;
}
function filterBeforeCursor(messages, beforeTs, beforeSeq) {
    const hasBeforeTs = Number.isFinite(Number(beforeTs)) && Number(beforeTs) > 0;
    const hasBeforeSeq = Number.isFinite(Number(beforeSeq));
    if (!hasBeforeTs && !hasBeforeSeq)
        return messages;
    const tsCutoff = Number(beforeTs || 0);
    const seqCutoff = hasBeforeSeq ? Number(beforeSeq) : null;
    return messages.filter((message) => {
        const messageSeq = Number.isFinite(Number(message.seq)) ? Number(message.seq) : null;
        if (seqCutoff !== null && messageSeq !== null)
            return messageSeq < seqCutoff;
        return Number(message.ts || 0) < tsCutoff;
    });
}
//# sourceMappingURL=chatRepository.js.map